import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Calendar, Gauge, Weight, BadgeCheck, Star, ShieldCheck, Heart, MessageCircle } from "lucide-react";
import { formatINR, formatMonthlyRent, BRAND } from "@/lib/constants";
import { toast } from "sonner";

const WISHLIST_KEY = "ehi_wishlist";
const readWishlist = () => {
  try { return JSON.parse(localStorage.getItem(WISHLIST_KEY) || "[]"); } catch { return []; }
};
const toggleWishlist = (id) => {
  const cur = readWishlist();
  const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
  localStorage.setItem(WISHLIST_KEY, JSON.stringify(next));
  return next.includes(id);
};

export default function EquipmentCard({ item }) {
  const isRental = item.listing_type !== "sale";
  const [saved, setSaved] = useState(() => readWishlist().includes(item.id));

  const handleFav = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const now = toggleWishlist(item.id);
    setSaved(now);
    toast.success(now ? "Saved to wishlist" : "Removed from wishlist");
  };

  const handleWa = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const msg = encodeURIComponent(`Hi EquipmentHub India, I'm interested in listing "${item.title}" (${item.id}). Please share warm intro.`);
    window.open(`https://wa.me/${BRAND.whatsappNumber}?text=${msg}`, "_blank", "noopener");
  };

  return (
    <Link
      to={`/equipment/${item.id}`}
      className="group flex flex-col fade-up bg-white border border-slate-200 hover:border-ehi-primary rounded-sm overflow-hidden transition-[border-color,box-shadow,transform] duration-200 hover:shadow-[0_10px_30px_-12px_rgba(11,31,58,0.25)] hover:-translate-y-0.5"
      data-testid={`equipment-card-${item.id}`}
    >
      <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden">
        {item.image_urls?.[0] ? (
          <img
            src={item.image_urls[0]}
            alt={item.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-slate-400 text-sm">No image</div>
        )}
        {/* Vertical navy hairline anchoring image to card body */}
        <div className="absolute inset-x-0 bottom-0 h-px bg-ehi-primary/70" aria-hidden />

        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {item.featured && (
            <span className="bg-ehi-accent text-white text-[10px] tracking-[0.15em] uppercase px-2 py-1 rounded-sm font-bold shadow-md font-mono-tab">
              ★ Featured
            </span>
          )}
          <span className="bg-white/95 backdrop-blur text-[10px] tracking-[0.15em] uppercase px-2 py-1 rounded-sm font-bold text-ehi-primary shadow-md">
            {item.listing_type === "sale" ? "For Sale" : "For Rent"}
          </span>
        </div>
        {/* Wishlist heart — top right */}
        <button
          onClick={handleFav}
          aria-label={saved ? "Remove from wishlist" : "Save to wishlist"}
          className={`absolute top-3 right-3 w-9 h-9 rounded-sm grid place-items-center shadow-md transition-[background-color,transform] duration-200 ${
            saved ? "bg-red-600 text-white" : "bg-white/95 text-slate-500 hover:text-red-600 hover:scale-110"
          }`}
          data-testid={`wishlist-${item.id}`}
        >
          <Heart className={`w-4 h-4 ${saved ? "fill-white" : ""}`} />
        </button>
        {item.vendor_verified && (
          <div className="absolute bottom-3 right-3 bg-emerald-700 text-white rounded-sm pl-1.5 pr-2 py-1 flex items-center gap-1 shadow-md" title="Verified Supplier · KYC checked">
            <BadgeCheck className="w-3.5 h-3.5" />
            <span className="text-[10px] font-bold tracking-[0.15em] font-mono-tab">VERIFIED</span>
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-2">
          <span className="ds-eyebrow text-[10px]">{item.category?.replace("-", " ")}</span>
          {item.rating > 0 && (
            <span className="ml-auto flex items-center gap-1 text-xs text-slate-600 font-mono-tab">
              <Star className="w-3 h-3 fill-ehi-accent text-ehi-accent" />
              {item.rating.toFixed(1)}
            </span>
          )}
        </div>
        <h3 className="font-display font-bold text-slate-900 leading-snug line-clamp-2 group-hover:text-ehi-primary transition-colors tracking-tight">
          {item.title}
        </h3>
        <div className="mt-1.5 text-[11px] text-slate-500 flex items-center gap-1 truncate uppercase tracking-wider" title="Supplier identity protected · Unlock via EHI">
          <ShieldCheck className="w-3 h-3 text-emerald-700 shrink-0" strokeWidth={2.2} />
          <span className="truncate">{item.vendor_verified ? "Verified Supplier" : "Supplier"} · Identity protected</span>
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mt-3 text-xs text-slate-600 font-mono-tab">
          <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-slate-400" />{item.year || "—"}</div>
          <div className="flex items-center gap-1.5"><Weight className="w-3.5 h-3.5 text-slate-400" />{item.capacity || "—"}</div>
          <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-slate-400" />{item.city || "—"}</div>
          <div className="flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5 text-slate-400" />{item.hours_used ? `${item.hours_used} hrs` : "—"}</div>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="flex items-end justify-between mb-2.5">
            <div>
              <div className="ds-eyebrow text-[10px]">
                {isRental ? "Monthly Rent" : "Asking Price"}
              </div>
              <div className="font-display text-xl font-black text-ehi-primary font-mono-tab tracking-tight leading-none mt-1">
                {isRental ? formatMonthlyRent(item) : formatINR(item.price)}
              </div>
            </div>
          </div>
          {/* Split CTA — primary Enquire (navy) + WhatsApp anchor (green) */}
          <div className="flex gap-2">
            <span className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm bg-ehi-primary text-white text-sm font-bold uppercase tracking-wider transition-colors group-hover:bg-ehi-accent" data-testid={`enquire-${item.id}`}>
              Enquire →
            </span>
            <button
              onClick={handleWa}
              aria-label="Enquire on WhatsApp"
              className="w-9 h-9 rounded-sm grid place-items-center bg-[#25D366] text-white hover:bg-[#1eaa53] transition-colors shrink-0"
              data-testid={`wa-${item.id}`}
            >
              <MessageCircle className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}
